/**
 * metaTrackingConfig.js
 *
 * Responsibility: Load tracking feature flags from DB (or env fallback).
 *
 * Key rule: always call `await metaConfig.ready()` before reading any flag.
 *
 * Do NOT put pixel or event logic here.
 */

import { getPublicKey } from '../api/api';

const DEFAULT_PAGE_FLAGS = {
  landingPage:     true,
  orderSuccess:    true,
  fallbackPayment: true,
  walletRecharge:  false,
  profileCreation: false,
};

class MetaTrackingConfig {
  constructor() {
    this._isEnabled     = false;
    this._pixelId       = null;
    this._trackingPages = { ...DEFAULT_PAGE_FLAGS };
    // _ready resolves once config is loaded — await ready() before checking flags
    this._ready = this._loadConfig();
  }

  // ─── Private loader ──────────────────────────────────────────────────────────
  async _loadConfig() {
    try {
      const [enabledRaw, pixelRaw, pagesRaw] = await Promise.all([
        getPublicKey('META_TRACKING_ENABLED'),
        getPublicKey('FACEBOOK_PIXEL_ID'),
        getPublicKey('META_TRACKING_PAGES'),
      ]);

      if (enabledRaw) this._isEnabled = enabledRaw === 'true';
      if (pixelRaw)   this._pixelId   = pixelRaw;
      if (pagesRaw) {
        try {
          this._trackingPages = { ...DEFAULT_PAGE_FLAGS, ...JSON.parse(pagesRaw) };
        } catch (_) { /* keep defaults on parse error */ }
      }
    } catch (_) {
      // DB unavailable — fall back to environment variables
      this._isEnabled = process.env.REACT_APP_META_TRACKING_ENABLED === 'true';
      this._pixelId   = process.env.REACT_APP_FACEBOOK_PIXEL_ID    || null;
    }
  }

  // ─── Public API ──────────────────────────────────────────────────────────────

  /** Await this before calling any getter to ensure config is loaded. */
  ready() { return this._ready; }

  /** True only when tracking is globally ON and a pixel ID exists. */
  isTrackingEnabled() {
    return this._isEnabled && !!this._pixelId;
  }

  /** True when globally ON and the specific page is allowed. */
  isPageTrackingEnabled(pageName) {
    return this.isTrackingEnabled() && !!this._trackingPages[pageName];
  }

  /** Returns the Facebook Pixel ID, or null if not configured. */
  getPixelId() { return this._pixelId; }

  /** Debug snapshot — useful for troubleshooting. */
  getDebugInfo() {
    return {
      isEnabled:     this._isEnabled,
      pixelId:       this._pixelId ? `${this._pixelId.slice(0, 8)}...` : null,
      trackingPages: this._trackingPages,
      userAgent:     navigator.userAgent,
      url:           window.location.href,
      timestamp:     new Date().toISOString(),
    };
  }
}

// Singleton — one instance shared across the entire app
export default new MetaTrackingConfig();